import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { AppNav } from "@/components/navigation/AppNav";
import { AppShell } from "@/components/navigation/AppShell";
import { PUBLIC_ROUTES } from "@/lib/navigation";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";
import TheScout from "@/components/accelerator/TheScout";
import SearchIndexingControl from "@/components/SearchIndexingControl";
import NotFound from "@/pages/NotFound";
import thankYouBg from "@/assets/thank-you-bg.jpg";
import { initSentry, Sentry, sentryEnabled } from "@/lib/sentry";
import { reportLovableError } from "@/lib/lovable-error-reporting";

const OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/rY5prBLnxNNzwAxt7cpRjQpXiXw1/social-images/social-1774957899884-maven-werx-logo-transparent.webp";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Mavenwerx" },
      { name: "description", content: "Mavenwerx Digital Dual Use Accelerator" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Mavenwerx" },
      { property: "og:description", content: "Mavenwerx Digital Dual Use Accelerator" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mavenwerx" },
      { name: "twitter:description", content: "Mavenwerx Digital Dual Use Accelerator" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@400;500;600&family=Oxanium:wght@600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** App chrome is only for signed-in areas — public marketing/forms stay clean. */
function GlobalNav() {
  const { pathname } = useLocation();
  if (PUBLIC_ROUTES.includes(pathname)) return null;
  return <AppNav />;
}

/** Scout chatbot lives only inside the portal, never on public pages. */
function PortalOnlyScout() {
  const { pathname } = useLocation();
  if (PUBLIC_ROUTES.includes(pathname)) return null;
  return <TheScout />;
}

function useBrowserErrorReporting() {
  // ported from main.tsx — runs once in the browser only
  useEffect(() => {
    initSentry();
    const onError = (event: ErrorEvent) => {
      console.error("[window.error]", event.message, `\nat ${event.filename}:${event.lineno}:${event.colno}`, "\nStack:\n", event.error?.stack ?? "(no stack)");
      if (sentryEnabled) {
        Sentry.captureException(event.error ?? new Error(event.message), {
          tags: { source: "window.error" },
          extra: { filename: event.filename, lineno: event.lineno, colno: event.colno },
        });
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      console.error("[unhandledrejection]", reason?.message ?? String(reason), "\nStack:\n", reason?.stack ?? "(no stack)");
      if (sentryEnabled) {
        Sentry.captureException(reason instanceof Error ? reason : new Error(String(reason)), {
          tags: { source: "unhandledrejection" },
        });
      }
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useBrowserErrorReporting();
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <div className="min-h-screen relative">
              <img src={thankYouBg} alt="" aria-hidden="true" className="fixed inset-0 w-full h-full object-cover z-0" />
              <div className="fixed inset-0 bg-gradient-to-br from-[hsl(220,60%,13%)] to-[hsl(220,50%,25%)] opacity-[0.72] z-0" />
              <div className="relative z-10">
                <AppShell>
                  <SearchIndexingControl />
                  <GlobalNav />
                  <Outlet />
                </AppShell>
              </div>
              <PortalOnlyScout />
            </div>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}

function RootError({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-bold">This page didn't load</h1>
        <p className="text-muted-foreground">Something went wrong. Please try again.</p>
        <div className="flex justify-center gap-3">
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset?.();
            }}
          >
            Try again
          </button>
          <Link to="/" className="rounded-md border border-border px-4 py-2">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}
